import Infonoise from "./modules/Infonoise.js";
import Alpha from './modules/Alpha.js';
import Comments from './modules/Comments.js';

import {getFirstPagePosts} from "./api/getFirstPagePosts";
import {getNextPagePosts} from "./api/getNextPagePosts";
import {getChannelsList} from "./api/getChannelsList";
import {getFilteredPosts} from "./api/getFilteredPosts.js";

const ContentSystem = {
  namespaced: true,

  state: {
    //Для списка постов
    isLoading: false,
    isReady: false,
    currentAbortController: null,
    nextPageUrl: '',
    lastVisitedUrl: '',
    isAdaptiveNews: true,
    isAdaptiveAlpha: false,
    //Для каналов
    channelsList: {},
    idChannelActive: null,
    isFilteredDataLoading: false,
    isEditChannels: false,
    isActivePodcastFilter: false,
    idEditPostChannels: null,
    //Для сигналов
    lastSignals: [],
  },

  actions: {
    //Получаем первые посты
    async toInitContent({commit, dispatch}) {
      commit('toToggleLoadingStatus', true);
      try {
        await dispatch('toGetFirstPagePosts');
        await dispatch('toGetChannels');
        commit('toToggleReadyStatus', true);
      } catch (error) {
        console.error(`Ошибка при инициализации контента: ${error}`);
      } finally {
        commit('toToggleLoadingStatus', false);
      }
    },
    //Получить посты главной страницы
    async toGetFirstPagePosts({state, commit, dispatch}) {
      if (state.currentAbortController) state.currentAbortController.abort();
      state.currentAbortController = new AbortController();
      const {signal} = state.currentAbortController;
      commit('toSetLoadingFilteredData', true);
      try {
        const data = await getFirstPagePosts({signal});
        dispatch('refreshLastSignals', data.signals);
        commit('Alpha/clearData');
        commit('Infonoise/clearData');
        dispatch('Alpha/addItemsToList', {alpha: data.alpha});
        dispatch('Infonoise/addItemsToList', {infonoise: data.news});
        commit('toUpdateNextPageUrl', data.next);
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error(`Ошибка при получении первой страницы постов: ${error}`);
        }
      } finally {
        dispatch('Alpha/loadAlphaData');
        dispatch('Infonoise/loadInfonoiseData');
        commit('toSetLoadingFilteredData', false);
        state.currentAbortController = null;
      }
    },
    //Получить посты следующей страницы
    async toGetNextPagePosts({state, commit, dispatch}) {
      commit('toToggleLoadingStatus', true);
      try {
        const data = await getNextPagePosts(state.nextPageUrl);
        dispatch('Alpha/addItemsToList', {alpha: data.alpha});
        dispatch('Infonoise/addItemsToList', {infonoise: data.news});
        commit('toUpdateNextPageUrl', data.next);
      } catch (error) {
        console.error(`Ошибка при получении первой страницы постов: ${error}`);
      } finally {
        commit('toToggleLoadingStatus', false);
        dispatch('Alpha/loadAlphaData');
        dispatch('Infonoise/loadInfonoiseData');
      }
    },
    //Получить отфильтрованные данные
    async toGetFilteredPosts({commit, dispatch, state}) {
      if (state.currentAbortController) state.currentAbortController.abort();
      state.currentAbortController = new AbortController();
      const {signal} = state.currentAbortController;
      commit('toSetLoadingFilteredData', true);
      try {
        const slug = state.channelsList[state.idChannelActive].slug;
        const data = await getFilteredPosts(slug, {signal});
        commit('Alpha/clearData');
        commit('Infonoise/clearData');
        dispatch('Alpha/addItemsToList', {alpha: data.alpha});
        dispatch('Infonoise/addItemsToList', {infonoise: data.news});
        commit('toUpdateNextPageUrl', data.next);
        commit('toSetPodcastFilterStatus', (state.channelsList[state.idChannelActive].isPodcast) ? true : false);
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error(`Ошибка при получении отфильтрованных постов: ${error}`);
        }
      } finally {
        await dispatch('Alpha/loadAlphaData');
        await dispatch('Infonoise/loadInfonoiseData');
        commit('toSetLoadingFilteredData', false);
        state.currentAbortController = null;
      }
    },
    //Получить список каналов
    async toGetChannels({commit}) {
      try {
        const data = await getChannelsList();
        commit('toPrepareChannels', data);
        commit('toAddPodcastsChannel');
        commit('toAddBookmarkChannel');
        commit('toAddAdminChannel');
      } catch (error) {
        console.error(`Ошибка при получении первой страницы постов: ${error}`);
      } finally {

      }
    },
    //Обновить последние сигналы
    async refreshLastSignals({commit}, data) {
      commit('saveLastSignals', data);
    }
  },

  mutations: {
    //Переключить статус индикатора загрузки данных
    toToggleLoadingStatus(state, status) {
      state.isLoading = (typeof status !== undefined) ? status : !state.isLoading;
    },
    //Переключить статус индикатора готовности данных
    toToggleReadyStatus(state, status) {
      state.isReady = (typeof status !== undefined) ? status : !state.isReady;
    },
    //Обновить адресс следующей страницы
    toUpdateNextPageUrl(state, url) {
      state.nextPageUrl = url;
    },
    //Запомнить последний посещенный маршрут
    toRememberLastPageUrl(state, url) {
      state.lastVisitedUrl = url;
    },
    //Поменять статус отображения колонки новостей в адаптиве
    toChangeNewsVisible(state) {
      state.isAdaptiveNews = true;
      state.isAdaptiveAlpha = false;
    },
    //Поменять статус отображения колонки альфа в адаптиве
    toChangeAlphaVisible(state) {
      state.isAdaptiveNews = false;
      state.isAdaptiveAlpha = true;
    },
    //Положить ID активного канала
    toSetIdActiveChannel(state, id) {
      state.idChannelActive = id;
    },
    //Измененит статус процесса загрузки отфильтированных данных
    toSetLoadingFilteredData(state, status) {
      state.isFilteredDataLoading = status;
    },
    //Отметить/ снять отметку, что используется фильтр по подкастам
    toSetPodcastFilterStatus(state, status) {
      state.isActivePodcastFilter = status;
    },
    //Сохранить последние сигналы для вывода в инфошуме
    saveLastSignals(state, data) {
      if (data) {
        data.forEach(item => {
          const exists = state.lastSignals.some(signal => signal.id == item.id);
          if (!exists) {
            if(state.lastSignals.length > 7) state.lastSignals.pop();
            state.lastSignals.push(item);
          }
        });
      }
    },
    //Подготовить данные для вывода каналов
    toPrepareChannels(state, channels) {
      channels.forEach(item => {
        state.channelsList[item.id] = {
          ...item,
          is_active: false,
          is_search_result: true,
        }
      });

    },
    //Включить/отключить режим редактирования каналов для редактора
    toToggleChannelMode(state, {is_channels_mode, object_id}) {
      state.isEditChannels = is_channels_mode || false;
      state.idEditPostChannels = object_id;
    },
    //Добавить канал 'Избранное'
    toAddBookmarkChannel(state) {
      const list = state.channelsList;
      const bookmarkID = Object.keys(list).length + 1;
      state.channelsList[bookmarkID] = {
        id: bookmarkID,
        is_active: false,
        is_search_result: true,
        name: 'Избранное',
        slug: 'bookmarks',
        priority_show: false,
        isBookmark: true,
      }
    },
    //Добавить канал 'Администрирование'
    toAddAdminChannel(state) {
      const list = state.channelsList;
      const adminID = Object.keys(list).length + 1;
      state.channelsList[adminID] = {
        id: adminID,
        is_active: false,
        is_search_result: true,
        name: 'Администрирование',
        slug: 'moderation',
        priority_show: false,
        isAdmin: true,
      }
    },
    //Добавить канал 'Подкасты'
    toAddPodcastsChannel(state) {
      const list = state.channelsList;
      const podcastID = Object.keys(list).length + 1;
      state.channelsList[podcastID] = {
        id: podcastID,
        logo: require('@shared/images/smiles/zipper.png'),
        is_active: false,
        is_search_result: true,
        name: 'Подкасты',
        slug: 'podcasts',
        priority_show: true,
        isPodcast: true,
      }
    },
  },

  modules: {
    Alpha,
    Infonoise,
    Comments,
  },
}

export default ContentSystem;
